// Session Memory Management Systems for Executive Coaching Protocol
// Manages conversation continuity, session history, and coaching context

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SessionMemory {
  lastKeyInsight?: string;
  lastTension?: string;
  lastDecision?: string;
  agreedAction?: string;
  agreedReflection?: string;
  sessionCount: number;
  lastSessionDate?: string;
  coreThemes: string[];
  engagementLevel: 'high' | 'medium' | 'low';
  detectedPatterns: string[];
}

export interface CoachingContext {
  coachId: string;
  sessionMemory: SessionMemory;
  messageHistory: Array<{
    id: string;
    text: string;
    sender: 'user' | 'ai';
    timestamp: Date;
    coachId?: string;
  }>;
  currentEngagementLevel: 'high' | 'medium' | 'low';
  detectedPatterns: string[];
  lastBlindSpotSurfaced?: string;
  lastBoundaryCheck?: string;
  interventionCount: number;
}

interface SessionMemoryState {
  // Session memories by sessionId and coachId
  sessionMemories: Record<string, Record<string, SessionMemory>>;
  
  // Current coaching contexts by sessionId and coachId
  coachingContexts: Record<string, Record<string, CoachingContext>>;
  
  // Actions
  getSessionMemory: (sessionId: string, coachId: string) => SessionMemory;
  updateSessionMemory: (sessionId: string, coachId: string, updates: Partial<SessionMemory>) => void;
  getCoachingContext: (sessionId: string, coachId: string) => CoachingContext;
  updateCoachingContext: (sessionId: string, coachId: string, updates: Partial<CoachingContext>) => void;
  addMessageToContext: (sessionId: string, coachId: string, message: {
    id: string;
    text: string;
    sender: 'user' | 'ai';
    timestamp: Date;
    coachId?: string;
  }) => void;
  incrementSessionCount: (sessionId: string, coachId: string) => void;
  resetSessionMemory: (sessionId: string, coachId?: string) => void;
}

const createDefaultSessionMemory = (): SessionMemory => ({
  sessionCount: 0,
  coreThemes: [],
  engagementLevel: 'high',
  detectedPatterns: []
});

const createDefaultCoachingContext = (coachId: string): CoachingContext => ({
  coachId,
  sessionMemory: createDefaultSessionMemory(),
  messageHistory: [],
  currentEngagementLevel: 'high',
  detectedPatterns: [],
  interventionCount: 0
});

export const useSessionMemoryStore = create<SessionMemoryState>()(
  persist(
    (set, get) => ({
      sessionMemories: {},
      coachingContexts: {},

      getSessionMemory: (sessionId: string, coachId: string) => {
        const state = get();
        if (!state.sessionMemories[sessionId]) {
          return createDefaultSessionMemory();
        }
        return state.sessionMemories[sessionId][coachId] || createDefaultSessionMemory();
      },

      updateSessionMemory: (sessionId: string, coachId: string, updates: Partial<SessionMemory>) => {
        set((state) => {
          const newState = { ...state };
          if (!newState.sessionMemories[sessionId]) {
            newState.sessionMemories[sessionId] = {};
          }
          
          const currentMemory = newState.sessionMemories[sessionId][coachId] || createDefaultSessionMemory();
          newState.sessionMemories[sessionId][coachId] = {
            ...currentMemory,
            ...updates,
            lastSessionDate: new Date().toISOString()
          };
          
          return newState;
        });
      },

      getCoachingContext: (sessionId: string, coachId: string) => {
        const state = get();
        if (!state.coachingContexts[sessionId]) {
          return createDefaultCoachingContext(coachId);
        }
        return state.coachingContexts[sessionId][coachId] || createDefaultCoachingContext(coachId);
      },

      updateCoachingContext: (sessionId: string, coachId: string, updates: Partial<CoachingContext>) => {
        set((state) => {
          const newState = { ...state };
          if (!newState.coachingContexts[sessionId]) {
            newState.coachingContexts[sessionId] = {};
          }
          
          const currentContext = newState.coachingContexts[sessionId][coachId] || createDefaultCoachingContext(coachId);
          newState.coachingContexts[sessionId][coachId] = {
            ...currentContext,
            ...updates
          };
          
          return newState;
        });
      },

      addMessageToContext: (sessionId: string, coachId: string, message) => {
        set((state) => {
          const newState = { ...state };
          if (!newState.coachingContexts[sessionId]) {
            newState.coachingContexts[sessionId] = {};
          }
          
          const currentContext = newState.coachingContexts[sessionId][coachId] || createDefaultCoachingContext(coachId);
          const updatedHistory = [...currentContext.messageHistory, message];
          
          // Keep only last 50 messages for performance
          if (updatedHistory.length > 50) {
            updatedHistory.splice(0, updatedHistory.length - 50);
          }
          
          newState.coachingContexts[sessionId][coachId] = {
            ...currentContext,
            messageHistory: updatedHistory
          };
          
          return newState;
        });
      },

      incrementSessionCount: (sessionId: string, coachId: string) => {
        const currentMemory = get().getSessionMemory(sessionId, coachId);
        get().updateSessionMemory(sessionId, coachId, {
          sessionCount: currentMemory.sessionCount + 1
        });
      },

      resetSessionMemory: (sessionId: string, coachId?: string) => {
        set((state) => {
          const newState = { ...state };
          
          if (coachId) {
            // Reset specific coach memory
            if (newState.sessionMemories[sessionId]) {
              delete newState.sessionMemories[sessionId][coachId];
            }
            if (newState.coachingContexts[sessionId]) {
              delete newState.coachingContexts[sessionId][coachId];
            }
          } else {
            // Reset entire session
            delete newState.sessionMemories[sessionId];
            delete newState.coachingContexts[sessionId];
          }
          
          return newState;
        });
      }
    }),
    {
      name: 'session-memory-storage',
      // Only persist session memories, not full contexts (too large)
      partialize: (state) => ({
        sessionMemories: state.sessionMemories
      })
    }
  )
);

// Utility functions for coaching protocol integration
export const extractCoreThemes = (messages: Array<{text: string; sender: string}>): string[] => {
  const userMessages = messages.filter(m => m.sender === 'user');
  const allText = userMessages.map(m => m.text).join(' ').toLowerCase();
  
  // Extract key themes using simple keyword analysis
  const themes: string[] = [];
  const themePatterns = {
    'leadership': /lead|manage|team|people|decision|strategy/g,
    'performance': /result|goal|execute|deliver|output|productivity/g,
    'career': /career|promotion|role|position|growth|development/g,
    'transition': /change|new|transition|move|shift|different/g,
    'balance': /balance|stress|overwhelm|time|energy|life/g,
    'communication': /communicate|conversation|feedback|meeting|discuss/g
  };
  
  for (const [theme, pattern] of Object.entries(themePatterns)) {
    const matches = allText.match(pattern);
    if (matches && matches.length >= 2) {
      themes.push(theme);
    }
  }
  
  return themes;
};

export const detectEngagementLevel = (messages: Array<{text: string; sender: string}>): 'high' | 'medium' | 'low' => {
  const userMessages = messages.filter(m => m.sender === 'user').slice(-5);
  
  if (userMessages.length === 0) return 'high';
  
  const avgLength = userMessages.reduce((sum, m) => sum + m.text.split(' ').length, 0) / userMessages.length;
  const hasDetailedResponses = userMessages.some(m => m.text.split(' ').length > 20);
  const hasQuestions = userMessages.some(m => m.text.includes('?'));
  
  if (avgLength > 15 && (hasDetailedResponses || hasQuestions)) {
    return 'high';
  } else if (avgLength > 8) {
    return 'medium';
  } else {
    return 'low';
  }
};

export const updateEngagementTracking = (
  sessionId: string,
  coachId: string,
  messages: Array<{text: string; sender: string}>
) => {
  const store = useSessionMemoryStore.getState();
  const themes = extractCoreThemes(messages);
  const engagementLevel = detectEngagementLevel(messages);
  
  store.updateSessionMemory(sessionId, coachId, {
    coreThemes: themes,
    engagementLevel
  });
};
